// frontend/src/components/formularios/ModalAdjuntosDocumentacion.jsx
// Modal para ver, agregar y eliminar los adjuntos de un registro de
// Documentación (puede tener varios archivos: póliza, comprobante, etc).
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal.jsx';
import AttachmentUploader from '../common/AttachmentUploader.jsx';
import { PrimaryButton } from '../common/Buttons';
import apiClient from '../../services/apiClient';
import { formatDateDisplay } from '../../utils/dateUtils';
import { iconForContentType, formatFileSize } from '../../utils/fileDisplay';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
`;

// Header propio del modal (se usa Modal sin `title` para poder construir
// una jerarquía real en vez de un string plano "Tipo · Equipo · Vto: fecha").
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 1rem 1.5rem;
  padding-right: 3rem; /* deja lugar al botón de cierre absoluto del Modal */
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const HeaderMain = styled.div`
  min-width: 0;
`;

const HeaderTitle = styled.h3`
  margin: 0 0 2px;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const HeaderSub = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

const VencimientoBadge = styled.div`
  flex-shrink: 0;
  text-align: right;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.txtMuted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 700;

  span {
    display: block;
    margin-top: 2px;
    font-size: 0.88rem;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.colors.txtPrincipal};
  }
`;

const Body = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

// Lista de archivos ya cargados — tabla-lista sin grilla (mismo lenguaje que
// TasksTable): separadores horizontales, sin bordes verticales, hover sutil.
const FileList = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  transition-property: background-color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const FileIcon = styled.i`
  font-size: 1.15rem;
  color: ${({ theme }) => theme.colors.txtMuted};
  flex-shrink: 0;
  width: 22px;
  text-align: center;
`;

const FileInfo = styled.a`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;

  &:hover span:first-child {
    text-decoration: underline;
  }
`;

const FileName = styled.span`
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileMeta = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.txtMuted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  flex-shrink: 0;
  transition-property: background-color, color;
  transition-duration: 0.15s;
  transition-timing-function: ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.colors.dangerBg || theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.danger};
  }
`;

const EmptyState = styled.div`
  padding: 1.25rem 0;
  text-align: center;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.txtMuted};
`;

const ErrorText = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.danger};
`;

const ModalAdjuntosDocumentacion = ({ isOpen, onClose, documento, onChanged }) => {
  const [adjuntos, setAdjuntos] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [huboCambios, setHuboCambios] = useState(false);

  useEffect(() => {
    setAdjuntos(documento?.adjuntos || []);
    setPendingFiles([]);
    setError('');
    setHuboCambios(false);
  }, [documento]);

  if (!documento) return null;

  const handleFilesSelected = (files) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleRemovePending = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubirPendientes = async () => {
    if (pendingFiles.length === 0) return;
    setSubiendo(true);
    setError('');
    try {
      const fd = new FormData();
      pendingFiles.forEach((f) => fd.append('archivos', f));
      const resp = await apiClient.post(`documentacion/${documento.id}/adjuntos/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAdjuntos((prev) => [...prev, ...(resp.data || [])]);
      setPendingFiles([]);
      setHuboCambios(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudieron subir los archivos.');
    } finally {
      setSubiendo(false);
    }
  };

  const handleEliminarExistente = async (adjuntoId) => {
    setError('');
    try {
      await apiClient.delete(`documentacion/${documento.id}/adjuntos/${adjuntoId}/`);
      setAdjuntos((prev) => prev.filter((a) => a.id !== adjuntoId));
      setHuboCambios(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo eliminar el adjunto.');
    }
  };

  const handleClose = () => {
    if (huboCambios && typeof onChanged === 'function') {
      onChanged();
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Wrap>
        <Header>
          <HeaderMain>
            <HeaderTitle>{documento.tipo_nombre}</HeaderTitle>
            <HeaderSub>Equipo {documento.equipo_interno}</HeaderSub>
          </HeaderMain>
          {documento.fecha_vencimiento && (
            <VencimientoBadge>
              Vencimiento
              <span>{formatDateDisplay(documento.fecha_vencimiento)}</span>
            </VencimientoBadge>
          )}
        </Header>

        <Body>
          <div>
            <SectionLabel>Archivos ({adjuntos.length})</SectionLabel>
            {adjuntos.length === 0 ? (
              <EmptyState>Todavía no hay archivos cargados en este documento.</EmptyState>
            ) : (
              <FileList>
                {adjuntos.map((adj) => (
                  <FileRow key={adj.id}>
                    <FileIcon className={iconForContentType(adj.archivo_content_type)} />
                    <FileInfo href={adj.archivo_url || adj.archivo} target="_blank" rel="noopener noreferrer">
                      <FileName>{adj.archivo_nombre_original || `Adjunto ${adj.id}`}</FileName>
                      {adj.archivo_size_bytes ? <FileMeta>{formatFileSize(adj.archivo_size_bytes)}</FileMeta> : null}
                    </FileInfo>
                    <RemoveButton type="button" title="Eliminar adjunto" onClick={() => handleEliminarExistente(adj.id)}>
                      <i className="bi bi-trash3"></i>
                    </RemoveButton>
                  </FileRow>
                ))}
              </FileList>
            )}
          </div>

          <div>
            <SectionLabel>Agregar archivos</SectionLabel>
            <AttachmentUploader
              label="Adjuntar archivos"
              pendingFiles={pendingFiles}
              onFilesSelected={handleFilesSelected}
              onRemovePending={handleRemovePending}
              disabled={subiendo}
            />
          </div>

          {error && <ErrorText>{error}</ErrorText>}

          {pendingFiles.length > 0 && (
            <PrimaryButton
              type="button"
              disabled={subiendo}
              onClick={handleSubirPendientes}
            >
              {subiendo ? 'Subiendo...' : `Subir ${pendingFiles.length} archivo(s)`}
            </PrimaryButton>
          )}
        </Body>
      </Wrap>
    </Modal>
  );
};

export default ModalAdjuntosDocumentacion;
